import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  LoadingBar,
  SwitchButton,
  TextInput,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Section = styled.div`
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
`;

const InfoLabel = styled(Typography)`
  color: #6b7a8d;
  min-width: 130px;
  flex-shrink: 0;
`;

const StatusBadge = styled.span<{ $variant: string }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 4px;
  background: ${(p) =>
    p.$variant === "Approved"
      ? "#e6f7ee"
      : p.$variant === "Pending"
      ? "#fff8e6"
      : p.$variant === "Declined"
      ? "#fde8e8"
      : "#f0f2f5"};
  color: ${(p) =>
    p.$variant === "Approved"
      ? "#1a7a42"
      : p.$variant === "Pending"
      ? "#946b00"
      : p.$variant === "Declined"
      ? "#c43d3d"
      : "#4a5568"};
`;

const OnboardingList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;
  font-size: 13px;
  color: #4a5568;
  li {
    margin-bottom: 4px;
  }
`;

const HintText = styled(Typography)`
  color: #8a9bb0;
`;

const DescText = styled(Typography)`
  color: #6b7a8d;
  line-height: 1.5;
`;

interface Props {
  companyId: string;
}

const RYFT_STATUS_APPROVED = "Approved";

function ryftVerificationLabel(status?: string): string {
  switch (status) {
    case "Approved":
      return "Approved";
    case "Pending":
      return "Pending review";
    case "Required":
      return "Verification required";
    case "Declined":
      return "Declined";
    case "NotRequired":
    case "NotCreated":
      return "Not started";
    default:
      return status ?? "Unknown";
  }
}

export const CompanyRyftCardFeesTab: React.FC<Props> = ({ companyId }) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [hasRyftAccount, setHasRyftAccount] = useState(false);
  const [ryftAccountId, setRyftAccountId] = useState<string | undefined>();
  const [ryftAccountStatus, setRyftAccountStatus] = useState<
    string | undefined
  >();
  const [ryftVerificationSnapshot, setRyftVerificationSnapshot] = useState<
    string | undefined
  >();
  const [ryftFrozen, setRyftFrozen] = useState(false);
  const [payoutMethodId, setPayoutMethodId] = useState<string | undefined>();
  const [requiredItemsSnapshot, setRequiredItemsSnapshot] = useState<
    | {
        personsStatus?: string;
        actionsRequired?: Array<{ action: string; description?: string }>;
        requiredFields?: Array<{ name: string }>;
        requiredDocuments?: Array<{
          category: string;
          types: string[];
          quantity: number;
        }>;
        refreshedAt?: string;
      }
    | undefined
  >();

  const [cardEnabled, setCardEnabled] = useState(false);
  const [togglingCard, setTogglingCard] = useState(false);

  const [feeActive, setFeeActive] = useState(false);
  const [feePct, setFeePct] = useState("0");
  const [feeFixed, setFeeFixed] = useState("0");
  const [savingAdminFee, setSavingAdminFee] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Use allSettled so a 404 on ryft-data doesn't block the admin fee load
    const [ryftResult, adminFeeResult] = await Promise.allSettled([
      api.getCompanyRyftData(companyId),
      api.getCardPaymentsAdministrativeFeeSettingsForCompany(companyId),
    ]);

    if (ryftResult.status === "fulfilled" && ryftResult.value) {
      const ryft = ryftResult.value;
      setHasRyftAccount(!!ryft.ryft_account_id);
      setRyftAccountId(ryft.ryft_account_id);
      setRyftAccountStatus(ryft.ryft_account_status);
      setRyftVerificationSnapshot(
        ryft.requiredItemsSnapshot?.verificationStatus
      );
      setRyftFrozen(!!ryft.frozen);
      setPayoutMethodId(ryft.payout_method_id);
      setRequiredItemsSnapshot(ryft.requiredItemsSnapshot);
      setCardEnabled(
        Boolean(
          ryft.ryftCardPaymentsEnabled && ryft.ryftPaymentStatus === "enabled"
        )
      );
    }

    if (adminFeeResult.status === "fulfilled") {
      const adminFee = adminFeeResult.value;
      setFeeActive(
        Boolean(adminFee.cardPaymentsBlendedAdministrativeFeeActive)
      );
      setFeePct(
        String(adminFee.cardPaymentsBlendedAdministrativeFeePercentage ?? 0)
      );
      setFeeFixed(
        String(adminFee.cardPaymentsBlendedAdministrativeFeeFixed ?? 0)
      );
    } else {
      showNotification(
        getApiErrorMessage(
          adminFeeResult.reason,
          "Could not load admin fee settings"
        ),
        NotificationTypes.WARNING
      );
    }

    setLoading(false);
  }, [companyId, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggleCard = async (val: boolean) => {
    if (val && ryftAccountStatus !== RYFT_STATUS_APPROVED) {
      showNotification(
        "Enable card payments only after the account is approved.",
        NotificationTypes.WARNING
      );
      return;
    }
    if (val && ryftFrozen) {
      showNotification(
        "Cannot enable card payments while the account is frozen.",
        NotificationTypes.WARNING
      );
      return;
    }
    setTogglingCard(true);
    try {
      await api.toggleCompanyCardPayments(companyId, val);
      setCardEnabled(val);
      showNotification(
        val
          ? "Card payments enabled for this merchant"
          : "Card payments disabled — customers cannot pay by Fena Card Payments",
        NotificationTypes.SUCCESS
      );
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Update failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setTogglingCard(false);
    }
  };

  const isRyftVerified = ryftAccountStatus === RYFT_STATUS_APPROVED;

  const handleSaveAdminFee = async () => {
    const p = Number(feePct);
    const f = Number(feeFixed);
    if (Number.isNaN(p) || p < 0 || p > 100) {
      showNotification("Percentage must be 0–100", NotificationTypes.WARNING);
      return;
    }
    if (Number.isNaN(f) || f < 0) {
      showNotification(
        "Fixed fee must be zero or greater",
        NotificationTypes.WARNING
      );
      return;
    }
    setSavingAdminFee(true);
    try {
      await api.updateCardPaymentsAdministrativeFeeSettingsForCompany(
        companyId,
        {
          cardPaymentsBlendedAdministrativeFeeActive: feeActive,
          cardPaymentsBlendedAdministrativeFeePercentage: p,
          cardPaymentsBlendedAdministrativeFeeFixed: f,
        }
      );
      showNotification("Saved", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Save failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setSavingAdminFee(false);
    }
  };

  if (loading) return <LoadingBar loading />;

  const hasOnboardingDetails =
    requiredItemsSnapshot &&
    ((requiredItemsSnapshot.actionsRequired?.length ?? 0) > 0 ||
      (requiredItemsSnapshot.requiredFields?.length ?? 0) > 0 ||
      (requiredItemsSnapshot.requiredDocuments?.length ?? 0) > 0);

  return (
    <Wrap>
      {/* ─ Ryft account details ─ */}
      {!hasRyftAccount && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Ryft Account Details
          </SectionTitle>
          <HintText variant={TypographyVariant.BODY4}>
            This company hasn&apos;t started card payments onboarding yet. Its
            account details will appear here once the merchant begins.
          </HintText>
        </Section>
      )}
      {hasRyftAccount && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Ryft Account Details
          </SectionTitle>
          <InfoRow>
            <InfoLabel variant={TypographyVariant.BODY4}>Account ID</InfoLabel>
            <Typography
              variant={TypographyVariant.BODY4}
              style={{ fontFamily: "monospace" }}
            >
              {ryftAccountId || "—"}
            </Typography>
          </InfoRow>
          <InfoRow>
            <InfoLabel variant={TypographyVariant.BODY4}>Status</InfoLabel>
            <StatusBadge $variant={ryftAccountStatus || ""}>
              {ryftVerificationLabel(ryftAccountStatus)}
            </StatusBadge>
          </InfoRow>
          {requiredItemsSnapshot?.personsStatus && (
            <InfoRow>
              <InfoLabel variant={TypographyVariant.BODY4}>Persons</InfoLabel>
              <Typography variant={TypographyVariant.BODY4}>
                {requiredItemsSnapshot.personsStatus}
              </Typography>
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel variant={TypographyVariant.BODY4}>
              Payout method
            </InfoLabel>
            <Typography
              variant={TypographyVariant.BODY4}
              style={{ fontFamily: "monospace" }}
            >
              {payoutMethodId || "Not connected"}
            </Typography>
          </InfoRow>
          {hasOnboardingDetails && (
            <>
              <HintText
                variant={TypographyVariant.BODY4}
                style={{ fontWeight: 600, color: "#946b00" }}
              >
                Onboarding items pending:
              </HintText>
              <OnboardingList>
                {requiredItemsSnapshot!.actionsRequired?.map((a, i) => (
                  <li key={`action-${i}`}>
                    <strong>{a.action}</strong>
                    {a.description ? ` — ${a.description}` : ""}
                  </li>
                ))}
                {requiredItemsSnapshot!.requiredFields?.map((f, i) => (
                  <li key={`field-${i}`}>Field: {f.name}</li>
                ))}
                {requiredItemsSnapshot!.requiredDocuments?.map((d, i) => (
                  <li key={`doc-${i}`}>
                    Document: {d.category} ({d.types.join(", ")})
                  </li>
                ))}
              </OnboardingList>
            </>
          )}
          {requiredItemsSnapshot?.refreshedAt && (
            <HintText variant={TypographyVariant.BODY7}>
              Last refreshed:{" "}
              {new Date(requiredItemsSnapshot.refreshedAt).toLocaleString()}
            </HintText>
          )}
        </Section>
      )}

      {/* ─ Card payments on/off ─ */}
      <Section>
        <SectionTitle variant={TypographyVariant.HEADER3}>
          Fena Card Payments
        </SectionTitle>
        {!hasRyftAccount ? (
          <HintText variant={TypographyVariant.BODY4}>
            No Fena Card Payments account linked to this company yet. Card
            payment controls appear after onboarding and approval.
          </HintText>
        ) : !isRyftVerified ? (
          <>
            <DescText variant={TypographyVariant.BODY4}>
              Card payments verification:{" "}
              <strong>{ryftVerificationLabel(ryftAccountStatus)}</strong>. The
              enable/disable control is available only after the account is
              approved. Fena Card Payments enables this automatically on
              approval; use the toggle below to turn card payments off from
              Fena&apos;s side once approved.
            </DescText>
            {ryftVerificationSnapshot && (
              <HintText variant={TypographyVariant.BODY4}>
                Latest card payments verification status:{" "}
                {ryftVerificationSnapshot}
              </HintText>
            )}
          </>
        ) : (
          <>
            <DescText variant={TypographyVariant.BODY4}>
              Account approved for Fena Card Payments. Use this switch to allow
              or block card checkout on Fena (updates{" "}
              <code>ryftCardPaymentsEnabled</code> on the company). Disabling
              stops new card payment sessions; KYB status is unchanged.
            </DescText>
            {ryftFrozen && (
              <HintText variant={TypographyVariant.BODY4}>
                Account is frozen — card payments cannot be enabled until the
                account is unfrozen.
              </HintText>
            )}
            <SwitchRow>
              <SwitchButton
                checked={cardEnabled}
                onChange={(val: boolean) => void handleToggleCard(val)}
                disabled={togglingCard || (ryftFrozen && !cardEnabled)}
              />
              <Typography variant={TypographyVariant.BODY3}>
                {cardEnabled
                  ? "Card payments enabled"
                  : "Card payments disabled (Fena gate)"}
              </Typography>
            </SwitchRow>
          </>
        )}
      </Section>

      {/* ─ Admin blended fee ─ */}
      <Section>
        <SectionTitle variant={TypographyVariant.HEADER3}>
          Platform fee (applied to card payments)
        </SectionTitle>
        <DescText variant={TypographyVariant.BODY4}>
          An optional fee deducted from each card payment total (not added to
          what the customer pays). Formula: fee = (payment amount × percentage ÷
          100) + fixed amount. Use percentage only (fixed = 0), fixed only
          (percentage = 0), or both for a blended fee. Merchants see these
          values read-only in the toolkit under Settings → Card Fees.
        </DescText>
        <SwitchRow>
          <SwitchButton checked={feeActive} onChange={setFeeActive} />
          <Typography variant={TypographyVariant.BODY3}>
            Enable blended administrative fee
          </Typography>
        </SwitchRow>
        <TextInput
          label="Percentage deducted from payment total (%)"
          type="number"
          min={0}
          max={100}
          step="0.01"
          value={feePct}
          onChange={(e) => setFeePct(e.target.value)}
        />
        <TextInput
          label="Fixed amount deducted from payment total (£)"
          type="number"
          min={0}
          step="0.01"
          value={feeFixed}
          onChange={(e) => setFeeFixed(e.target.value)}
        />
        <Button
          variant={ButtonVariant.CONTAINED}
          onClick={() => void handleSaveAdminFee()}
          loading={savingAdminFee}
          loadingText="Saving"
        >
          Save platform fee
        </Button>
      </Section>
    </Wrap>
  );
};
